import { Box, Card, CardContent, Tooltip, Typography } from '@mui/material';
import { ProjectMenu } from 'src/components/ProjectMenu/ProjectMenu';
import { GetProjectResponseProject } from "src/model/api/v2/data-contracts";
import { ts } from "src/utils/translate";
import { getContrastColor } from "src/utils/labelColors";
import { ElectricalCabinetIcon } from "src/icons/quote/ElectricalCabinetIcon";
import { IsElectricalCabinetAvailable } from "src/pages/ProjectsPage/electricalCabinetAvailable";
import { getRelativeTime } from "src/utils/timeHelper";
import { theme } from "src/theme/mui-theme";
import { useNavigate } from "react-router";

type Props = {
    extendedProject: GetProjectResponseProject;
};

export function ProjectsGridCard({ extendedProject }: Props) {
    const labels = extendedProject.labels ?? [];
    const navigate = useNavigate();

    return (
        <Card
            onClick={() => navigate(`/planner/${extendedProject.project.id}`)}
            sx={{
                height: '100%',
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
                borderRadius: '12px',
                cursor: 'pointer',
            }}
        >
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography variant="h6" component="div" noWrap sx={{ flex: 1, mr: 1 }}>
                        {extendedProject.project.project_name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {IsElectricalCabinetAvailable(extendedProject) && (
                            <Tooltip title={ts('electrical_cabinet_purchased', 'Electrical cabinet purchased')} placement="top">
                                <Box
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: '50%',
                                        backgroundColor: theme.palette.primary.main,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <ElectricalCabinetIcon sx={{ fontSize: 20 }} />
                                </Box>
                            </Tooltip>
                        )}
                        <Box onClick={(e) => e.stopPropagation()} sx={{ transform: 'rotate(90deg)' }}>
                            <ProjectMenu extendedProject={extendedProject} />
                        </Box>
                    </Box>
                </Box>

                <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.secondary }}>
                    {ts('project_address_label', 'Address')}
                </Typography>
                <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                    {extendedProject.project.city} {extendedProject.project.street_address}
                </Typography>

                <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.secondary }}>
                    {ts('project_last_modified_label', 'Last modified')}
                </Typography>
                <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                    {getRelativeTime(extendedProject.project.updated_at)}
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {labels.slice(0, 2).map(label => (
                            <Box
                                key={label.id}
                                sx={{
                                    backgroundColor: label.color,
                                    color: getContrastColor(label.color),
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: '16px',
                                    fontSize: '0.75rem',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {label.name}
                            </Box>
                        ))}
                    </Box>
                    {labels.length > 2 && (
                        <Tooltip title={labels.slice(2).map(label => label.name).join(', ')} placement="top">
                            <Box
                                sx={{
                                    color: theme.palette.primary.main,
                                    fontSize: '0.75rem',
                                    whiteSpace: 'nowrap', 
                                    mr: 2,
                                    cursor: 'pointer',
                                }}
                            >
                                +{labels.length - 2}
                            </Box>
                        </Tooltip>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
}
